import { formatLocalHm } from './clock';

/** Portugal (Europe/Lisbon). The badge is hidden when a value cannot be read exactly. */
export const OPENING_HOURS_ZONE = 'Europe/Lisbon';

export type OpeningKind = 'open' | 'closed' | 'opens';

export interface OpeningStatus {
  kind: OpeningKind;
  /** "Open now", "Closed", or "Opens 9:00 AM". */
  label: string;
  /** 12-hour close of the span that is open now. Absent for a 24-hour span. */
  until?: string;
  /** The matching span covers the whole day (OSM `24/7`). */
  roundTheClock?: boolean;
  /** 12-hour lines. The first is the usual week; later lines are exceptions. */
  lines: string[];
}

/** Card line. Open places name the close; the week itself stays behind Show more. */
export function formatOpeningSummary(status: OpeningStatus): string {
  if (status.kind === 'open' && status.until) return `Open now · until ${status.until}`;
  if (status.kind === 'open' && status.roundTheClock) return 'Open now · 24 hours';
  return status.label;
}

const DAY_INDEX: Record<string, number> = { Mo: 0, Tu: 1, We: 2, Th: 3, Fr: 4, Sa: 5, Su: 6 };
const DAY_LABEL = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTH_INDEX: Record<string, number> = {
  Jan: 1, Feb: 2, Mar: 3, Apr: 4, May: 5, Jun: 6,
  Jul: 7, Aug: 8, Sep: 9, Oct: 10, Nov: 11, Dec: 12,
};

interface Span {
  start: number;
  end: number;
}

interface Rule {
  /** Highest when this rule matches the given civil day. Null when it does not apply. */
  specificity(year: number, month: number, day: number, weekday: number): number | null;
  off: boolean;
  spans: Span[];
  line: string;
}

interface Civil {
  year: number;
  month: number;
  day: number;
  weekday: number;
  minutes: number;
}

/** Gregorian computus. Month is 1–12. */
export function easterSunday(year: number): { month: number; day: number } {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return { month, day };
}

function civilInZone(date: Date, timeZone: string): Civil | null {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'short',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  const bag: Record<string, string> = {};
  for (const part of fmt.formatToParts(date)) {
    if (part.type !== 'literal') bag[part.type] = part.value;
  }
  const weekdayMap: Record<string, number> = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
  const weekday = weekdayMap[bag.weekday ?? ''];
  if (weekday === undefined) return null;
  return {
    year: Number(bag.year),
    month: Number(bag.month),
    day: Number(bag.day),
    weekday,
    minutes: Number(bag.hour) * 60 + Number(bag.minute),
  };
}

function addDays(year: number, month: number, day: number, delta: number): { year: number; month: number; day: number; weekday: number } {
  const utc = new Date(Date.UTC(year, month - 1, day + delta));
  const weekday = (utc.getUTCDay() + 6) % 7;
  return { year: utc.getUTCFullYear(), month: utc.getUTCMonth() + 1, day: utc.getUTCDate(), weekday };
}

function clockLabel(minutes: number): string {
  const normalized = ((minutes % 1440) + 1440) % 1440;
  const h = Math.floor(normalized / 60);
  const m = normalized % 60;
  return formatLocalHm(`${h}:${String(m).padStart(2, '0')}`);
}

function rangeLabel(span: Span): string {
  return `${clockLabel(span.start)}–${clockLabel(span.end)}`;
}

function weekdaySpanLabel(days: number[]): string {
  const runs: string[] = [];
  let i = 0;
  const sorted = [...days].sort((a, b) => a - b);
  while (i < sorted.length) {
    let j = i;
    while (j + 1 < sorted.length && sorted[j + 1] === sorted[j] + 1) j += 1;
    runs.push(i === j ? DAY_LABEL[sorted[i]] : `${DAY_LABEL[sorted[i]]}–${DAY_LABEL[sorted[j]]}`);
    i = j + 1;
  }
  return runs.join(', ');
}

interface Selector {
  specificity: number;
  matches(year: number, month: number, day: number, weekday: number): boolean;
  label: string;
}

function parseWeekdayToken(token: string): { days: number[] } | null {
  const range = token.match(/^(Mo|Tu|We|Th|Fr|Sa|Su)-(Mo|Tu|We|Th|Fr|Sa|Su)$/);
  if (range) {
    const start = DAY_INDEX[range[1]];
    const end = DAY_INDEX[range[2]];
    const days: number[] = [];
    if (start <= end) {
      for (let d = start; d <= end; d += 1) days.push(d);
    } else {
      for (let d = start; d <= 6; d += 1) days.push(d);
      for (let d = 0; d <= end; d += 1) days.push(d);
    }
    return { days };
  }
  if (DAY_INDEX[token] !== undefined) return { days: [DAY_INDEX[token]] };
  return null;
}

function parseSelectorList(raw: string): Selector | null {
  const tokens = raw.split(',').map((part) => part.trim()).filter(Boolean);
  if (tokens.length === 0) return null;
  const weekdays: number[] = [];
  const dates: { month: number; day: number; label: string }[] = [];
  let easter = false;
  for (const token of tokens) {
    if (token === 'easter') {
      easter = true;
      continue;
    }
    const dated = token.match(/^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{1,2})$/);
    if (dated) {
      const month = MONTH_INDEX[dated[1]];
      const day = Number(dated[2]);
      if (day < 1 || day > 31) return null;
      dates.push({ month, day, label: `${dated[1]} ${day}` });
      continue;
    }
    const week = parseWeekdayToken(token);
    if (!week) return null;
    weekdays.push(...week.days);
  }
  const labels: string[] = [];
  if (weekdays.length) labels.push(weekdaySpanLabel(weekdays));
  if (easter) labels.push('Easter Sunday');
  labels.push(...dates.map((d) => d.label));
  return {
    specificity: dates.length ? 4 : easter ? 3 : 1,
    label: labels.join(', '),
    matches(year, month, day, weekday) {
      if (weekdays.includes(weekday)) return true;
      if (easter) {
        const east = easterSunday(year);
        if (east.month === month && east.day === day) return true;
      }
      return dates.some((d) => d.month === month && d.day === day);
    },
  };
}

function parseSpans(raw: string): Span[] | null {
  const parts = raw.split(',').map((part) => part.trim()).filter(Boolean);
  if (parts.length === 0) return null;
  const spans: Span[] = [];
  for (const part of parts) {
    const match = part.match(/^(\d{1,2}):(\d{2})-(\d{1,2}):(\d{2})$/);
    if (!match) return null;
    const sh = Number(match[1]);
    const sm = Number(match[2]);
    const eh = Number(match[3]);
    const em = Number(match[4]);
    if (sm > 59 || em > 59 || sh > 24 || eh > 24) return null;
    if ((sh === 24 && sm !== 0) || (eh === 24 && em !== 0)) return null;
    const start = sh * 60 + sm;
    let end = eh * 60 + em;
    if (end <= start) end += 1440;
    spans.push({ start, end });
  }
  return spans;
}

function parseRule(raw: string): Rule | null {
  const text = raw.trim();
  if (!text) return null;
  if (text === '24/7') {
    return {
      specificity: () => 0,
      off: false,
      spans: [{ start: 0, end: 1440 }],
      line: 'Open 24 hours',
    };
  }
  const off = text.match(/^(.*)\soff$/);
  const body = off ? off[1].trim() : text;
  const timeMatch = body.match(/^(.*?)(\d{1,2}:\d{2}-\d{1,2}:\d{2}(?:\s*,\s*\d{1,2}:\d{2}-\d{1,2}:\d{2})*)$/);
  if (!off && !timeMatch) return null;
  const selectorRaw = (off ? body : timeMatch?.[1] ?? '').trim();
  const spans = off ? [] : parseSpans((timeMatch?.[2] ?? '').replace(/\s+/g, ''));
  if (!off && !spans) return null;
  const everyDay = !selectorRaw;
  const selector = everyDay
    ? {
        specificity: 0,
        label: 'Every day',
        matches: () => true,
      }
    : parseSelectorList(selectorRaw);
  if (!selector) return null;
  const hoursLabel = off || !spans ? 'Closed' : spans.map(rangeLabel).join(', ');
  return {
    specificity: (year, month, day, weekday) => (selector.matches(year, month, day, weekday) ? selector.specificity : null),
    off: Boolean(off),
    spans: spans ?? [],
    line: `${selector.label} ${hoursLabel}`,
  };
}

function parseSpec(spec: string): Rule[] | null {
  if (/[+"]|PH|SH|sunrise|sunset|week\s*\d|Jan-|Feb-|Mar-|Apr-|May-|Jun-|Jul-|Aug-|Sep-|Oct-|Nov-|Dec-/i.test(spec)) {
    return null;
  }
  const rules: Rule[] = [];
  for (const chunk of spec.split(';')) {
    const rule = parseRule(chunk);
    if (!rule) return null;
    rules.push(rule);
  }
  return rules.length ? rules : null;
}

function spansOnDay(rules: Rule[], year: number, month: number, day: number, weekday: number): Span[] {
  let best = -1;
  const chosen: Rule[] = [];
  for (const rule of rules) {
    const rank = rule.specificity(year, month, day, weekday);
    if (rank === null) continue;
    if (rank > best) {
      best = rank;
      chosen.length = 0;
    }
    if (rank === best) chosen.push(rule);
  }
  const spans: Span[] = [];
  for (const rule of chosen) {
    if (rule.off) spans.length = 0;
    else spans.push(...rule.spans);
  }
  return spans;
}

function contains(spans: Span[], minute: number): boolean {
  return spans.some((span) => minute >= span.start && minute < span.end);
}

/**
 * Spans that contain this minute, from today and from yesterday's overnight range.
 * `end` is minutes after today's midnight, so a later range on either day can win.
 */
function coveringSpans(todaySpans: Span[], yesterdaySpans: Span[], minutes: number): { end: number; duration: number }[] {
  const found: { end: number; duration: number }[] = [];
  for (const span of todaySpans) {
    if (minutes >= span.start && minutes < span.end) {
      found.push({ end: span.end, duration: span.end - span.start });
    }
  }
  for (const span of yesterdaySpans) {
    const shifted = minutes + 1440;
    if (shifted >= span.start && shifted < span.end) {
      found.push({ end: span.end - 1440, duration: span.end - span.start });
    }
  }
  return found;
}

/**
 * Read an OSM opening_hours value in Europe/Lisbon.
 * Returns null when the value is missing or not understood. Never guesses.
 */
export function describeOpeningHours(
  spec: string | undefined,
  now: Date,
  timeZone = OPENING_HOURS_ZONE,
): OpeningStatus | null {
  if (!spec?.trim()) return null;
  const rules = parseSpec(spec.trim());
  if (!rules) return null;
  const civil = civilInZone(now, timeZone);
  if (!civil) return null;

  const todaySpans = spansOnDay(rules, civil.year, civil.month, civil.day, civil.weekday);
  const yesterday = addDays(civil.year, civil.month, civil.day, -1);
  const yesterdaySpans = spansOnDay(rules, yesterday.year, yesterday.month, yesterday.day, yesterday.weekday);
  const open = contains(todaySpans, civil.minutes) || contains(yesterdaySpans, civil.minutes + 1440);

  let label = 'Closed';
  let kind: OpeningKind = 'closed';
  let until: string | undefined;
  let roundTheClock = false;
  if (open) {
    label = 'Open now';
    kind = 'open';
    const covering = coveringSpans(todaySpans, yesterdaySpans, civil.minutes);
    if (covering.some((span) => span.duration >= 1440)) roundTheClock = true;
    else if (covering.length > 0) until = clockLabel(Math.max(...covering.map((span) => span.end)));
  } else {
    const laterToday = [...todaySpans].sort((a, b) => a.start - b.start).find((span) => span.start > civil.minutes);
    if (laterToday) {
      label = `Opens ${clockLabel(laterToday.start)}`;
      kind = 'opens';
    }
  }

  return {
    kind,
    label,
    ...(until ? { until } : {}),
    ...(roundTheClock ? { roundTheClock } : {}),
    lines: rules.map((rule) => rule.line),
  };
}
